import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { cn } from '@/lib/cn';

export type ButtonVariant =
  | 'primary'
  | 'secondary'
  | 'ghost'
  | 'quickAction'
  | 'listeningIdle'
  | 'listeningActive';
export type ButtonSize = 'md' | 'sm' | 'icon';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  leftIcon?: ReactNode;
  rightSlot?: ReactNode;
}

const baseClasses =
  'inline-flex items-center justify-center gap-2 rounded-md font-sans font-medium ' +
  'transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-700/60 ' +
  'disabled:cursor-not-allowed disabled:opacity-50';

const variantClasses: Record<ButtonVariant, string> = {
  primary: 'bg-amber-600 text-white hover:bg-amber-700',
  secondary: 'bg-stone-800 text-stone-200 hover:bg-stone-700',
  ghost: 'text-stone-400 hover:text-amber-400',
  quickAction:
    'border border-stone-800 bg-stone-900 text-stone-200 ' +
    'hover:border-stone-700 hover:bg-stone-800 [&_svg]:text-amber-500',
  listeningIdle: 'bg-emerald-600 text-white hover:bg-emerald-700',
  listeningActive: 'bg-red-600 text-white hover:bg-red-700',
};

const sizeClasses: Record<ButtonSize, string> = {
  md: 'px-4 py-2 text-sm',
  sm: 'px-3 py-1.5 text-xs',
  icon: 'h-9 w-9 p-0',
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'primary', size = 'md', leftIcon, rightSlot, className, children, ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={rest.type ?? 'button'}
      className={cn(baseClasses, variantClasses[variant], sizeClasses[size], className)}
      {...rest}
    >
      {leftIcon ? <span className="inline-flex shrink-0 items-center">{leftIcon}</span> : null}
      {children ? <span className="inline-flex items-center">{children}</span> : null}
      {rightSlot ? <span className="ml-auto inline-flex items-center">{rightSlot}</span> : null}
    </button>
  );
});
